import type { AttributeReferenceObjectType } from "src/schemas/attributeSchemas";

export const URLS = {
  setup: "/setup",
  setupGetStarted: "/setup/get-started",
  setupLocations: buildLocationsPath("/setup"),
  setupLocationGroups: buildLocationGroupsPath("/setup"),
  setupReaders: buildReadersPath("/setup"),
  setupAttributeDefinitions: buildAttributeDefinitionsPath("/setup"),
  setupDataTasks: buildDataTasksPath("/setup"),
  setupSensorGroups: buildSensorGroupsPath("/setup"),
  setupImport: "/setup/import",
  users: "/organization/users",
  apiKeys: "/organization/api-keys",
  locations: buildLocationsPath(),
  locationGroups: buildLocationGroupsPath(),
  sensorGroups: buildSensorGroupsPath(),
  readers: buildReadersPath(),
  detectedReaders: buildDetectedReadersPath(),
  licences: "/configuration/licences",
  resources: "/data/resources",
  attributeDefinitions: buildAttributeDefinitionsPath(),
  traitDefinitions: "/configuration/trait-definitions",
  dataTasks: buildDataTasksPath(),
  sensors: "/data/sensors",
  sensorTraits: "/data/sensor-traits",
  floorplans: "/analysis/floorplans",
  settings: "/settings",
  login: "/login",
  overview: "/overview",
  configuration: "/organization/configuration",
  inventory: "/data/inventory",
  alerts: "/alerts",
  alertsAcknowledged: "/alerts/acknowledged",
  alertsHistorical: "/alerts/historical",
  analytics: "/analysis/analytics",
  locationSequences: "/configuration/location-sequences",
  attributeValues: "/data/attribute-values",
  search: "/search",
} as const;

export type UrlKey = keyof typeof URLS;

export function buildLocationsPath(rootPath: string = "/configuration") {
  return `${rootPath}/locations`;
}

export function buildLocationGroupsPath(rootPath: string = "/configuration") {
  return `${rootPath}/locations/groups`;
}

export function buildReadersPath(rootPath: string = "/configuration") {
  return `${rootPath}/readers`;
}

export function buildDetectedReadersPath(rootPath: string = "/configuration") {
  return `${rootPath}/readers/detected`;
}

export function buildAttributeDefinitionsPath(
  rootPath: string = "/configuration",
) {
  return `${rootPath}/attribute-definitions`;
}

export function buildDataTasksPath(rootPath: string = "/configuration") {
  return `${rootPath}/data-tasks`;
}

export function buildSensorGroupsPath(rootPath: string = "/configuration") {
  return `${rootPath}/groups`;
}

/** Root path of each object type an attribute value can be attached to. */
export const PARENT_PATH_MAP: Record<AttributeReferenceObjectType, string> = {
  location: URLS.locations,
  resource: URLS.resources,
  sensor: URLS.sensors,
  group: URLS.sensorGroups,
};

export function buildAttributeValueDetailsPath(
  valueId: string | number,
  parent?: AttributeValueParent,
) {
  if (parent) {
    return `${buildAttributeValuesPath(parent)}/${valueId}`;
  }
  return `${URLS.attributeValues}/${valueId}`;
}

export function buildAttributeValueEditPath(
  valueId: string | number,
  parent?: AttributeValueParent,
) {
  if (parent) {
    return `${buildAttributeValuesPath(parent)}/${valueId}/edit`;
  }
  return `${URLS.attributeValues}/${valueId}/edit`;
}

interface AttributeValueParent {
  type: AttributeReferenceObjectType;
  id: string | number;
}

const buildAttributeValuesPath = (parent: AttributeValueParent) =>
  `${PARENT_PATH_MAP[parent.type]}/${parent.id}/attribute-values`;

export function buildSensorTraitDetailsPath(
  id: string | number,
  sensorId?: string | number,
) {
  const path = `/sensor-traits/${id}`;
  if (sensorId !== undefined) {
    return `${URLS.sensors}/${sensorId}${path}`;
  }
  return `/data/${path}`;
}

export function buildSensorTraitIngestPath(
  id: string | number,
  sensorId?: string | number,
) {
  const path = `/sensor-traits/${id}/ingest`;
  if (sensorId !== undefined) {
    return `${URLS.sensors}/${sensorId}${path}`;
  }
  return `/data/${path}`;
}

export function buildSensorTraitEditPath(
  id: string | number,
  sensorId?: string | number,
) {
  const path = `/sensor-traits/${id}/edit`;
  if (sensorId !== undefined) {
    return `${URLS.sensors}/${sensorId}${path}`;
  }
  return `/data/${path}`;
}
